// frontend/src/components/IndicatorSelector.tsx

import React, { useState, useEffect } from 'react';
import type { Indicator } from '../state/types';
import { useAppStore } from '../state/store';
import IndicatorChooser from './IndicatorChooser';
import IndicatorEditorSMA from './editors/IndicatorEditorSMA';
import IndicatorEditorRSI from './editors/IndicatorEditorRSI';
import IndicatorEditorAdditionalInstrument from './editors/IndicatorEditorAdditionalInstrument';
import IndicatorEditorRolling from './editors/IndicatorEditorRolling';
import IndicatorEditorBarRange from './editors/IndicatorEditorBarRange';
import IndicatorEditorROC from './editors/IndicatorEditorROC';
import IndicatorEditorCarryForward from './editors/IndicatorEditorCarryForward';
import IndicatorEditorBollinger from './editors/IndicatorEditorBollinger';
import IndicatorEditorPercentB from './editors/IndicatorEditorPercentB';
import IndicatorEditorADX from './editors/IndicatorEditorADX';
import {
  getIndicatorDisplayName,
  indicatorDisplayRegistry,
} from '../utils/indicatorDisplayRegistry';
import {
  getSeriesLabelForIndicator,
  getRollingSeriesOptions,
} from './ExpressionEditor/expressionEditorSelectors';
import { getUniqueIndicatorName } from '../utils/indicatorNames';

type IndicatorSelectorProps = {
  onIndicatorAdd: (indicator: Indicator) => string;
};

const IndicatorSelector: React.FC<IndicatorSelectorProps> = ({ onIndicatorAdd }) => {
  const [choosers, setChoosers] = useState<{ id: number; editMode: boolean; indicatorId?: string }[]>([]);
  const [chooserCount, setChooserCount] = useState(0);
  const [addEnabled, setAddEnabled] = useState(true);

  const primaryInstrumentId = useAppStore((state) => state.primaryInstrumentId);
  const indicators = useAppStore((state) => state.indicators);

  // Sync choosers from store when indicators change (e.g. after loading a saved backtest)
  useEffect(() => {
    const hasPendingAdd = choosers.some((c) => c.indicatorId === undefined);
    if (hasPendingAdd) return;
    const storeIds = indicators.map((i) => i.id);
    const chooserIds = choosers
      .map((c) => c.indicatorId)
      .filter((id): id is string => id != null);
    const same =
      storeIds.length === chooserIds.length &&
      storeIds.every((id, i) => id === chooserIds[i]);
    if (!same) {
      setChoosers(
        indicators.map((ind, i) => ({
          id: i + 1,
          editMode: false,
          indicatorId: ind.id,
        }))
      );
      setChooserCount(indicators.length);
      setAddEnabled(true);
    }
  }, [indicators, choosers]);

  // Beautiful labels from registry
  const indicatorLabels = Object.fromEntries(
    Object.entries(indicatorDisplayRegistry).map(([key, info]) => [
      key,
      info.label || key,
    ])
  );

  // Store component constructors (not instances) — fixes TS errors
  const editorMap = {
    SMA: IndicatorEditorSMA,
    RSI: IndicatorEditorRSI,
    AdditionalInstrument: IndicatorEditorAdditionalInstrument,
    rolling: IndicatorEditorRolling,
    bar_range: IndicatorEditorBarRange,
    ROC: IndicatorEditorROC,
    CarryForward: IndicatorEditorCarryForward,
    BollingerBands: IndicatorEditorBollinger,
    PercentB: IndicatorEditorPercentB,
    ADX: IndicatorEditorADX,
  } as const;

  const handleAddClick = () => {
    if (addEnabled) {
      const nextId = chooserCount + 1;
      setChooserCount(nextId);
      setChoosers([...choosers, { id: nextId, editMode: true }]);
      setAddEnabled(false);
    }
  };

  const handleAccept = (id: number, type: string, config: unknown) => {
    const chooser = choosers.find((c) => c.id === id);
    const state = useAppStore.getState();
    const instruments = state.instruments;
    const indicators = state.indicators;

    if (chooser?.indicatorId) {
      const existing = indicators.find((i) => i.id === chooser.indicatorId);
      if (!existing) return;

      if (type === 'AdditionalInstrument') {
        const newSymbol = (config as { symbol: string }).symbol;
        const currentInstrument = instruments.find((i) => i.id === existing.instrumentId);
        const currentGenerated = currentInstrument?.symbol ?? existing.name;
        const name =
          existing.name === currentGenerated
            ? getUniqueIndicatorName(newSymbol, indicators, chooser.indicatorId)
            : existing.name;
        const updates: Partial<Omit<Indicator, 'id'>> = {
          name,
          type,
          config: config as Record<string, any>,
        };
        useAppStore.getState().updateIndicator(chooser.indicatorId, updates);
      } else {
        const symbol = getSeriesLabelForIndicator(existing, instruments, indicators);
        const options =
          type === 'rolling'
            ? getRollingSeriesOptions(existing.instrumentId, instruments, indicators)
            : undefined;
        const currentGenerated = getIndicatorDisplayName(existing, symbol, options);
        const newGenerated = getIndicatorDisplayName(
          { ...existing, config: config as Record<string, any> },
          symbol,
          options
        );
        const name =
          existing.name === currentGenerated
            ? getUniqueIndicatorName(newGenerated, indicators, chooser.indicatorId)
            : existing.name;
        const updates: Partial<Omit<Indicator, 'id'>> = {
          name,
          type,
          config: config as Record<string, any>,
          instrumentId: (config as { instrumentId: string }).instrumentId,
        };
        useAppStore.getState().updateIndicator(chooser.indicatorId, updates);
      }
      setChoosers((prev) =>
        prev.map((c) => (c.id === id ? { ...c, editMode: false } : c))
      );
      setAddEnabled(true);
      return;
    }

    // Add new indicator
    let returnedId: string;
    if (type === 'AdditionalInstrument') {
      const { symbol, adjusted } = config as { symbol: string; adjusted: boolean };

      if (!symbol.trim()) {
        alert("Symbol is required.");
        return;
      }

      const newId = useAppStore.getState().addInstrument(symbol, adjusted, { setAsPrimary: false });

      const newIndicator: Indicator = {
        id: crypto.randomUUID(),
        name: getUniqueIndicatorName(symbol, indicators),
        type: type,
        config: config as Record<string, any>,
        instrumentId: newId,
      };

      returnedId = onIndicatorAdd(newIndicator);
    } else {
      const typedConfig = config as { instrumentId: string };
      const minimalIndicator: Indicator = {
        id: '',
        name: '',
        type,
        config: config as Record<string, any>,
        instrumentId: typedConfig.instrumentId,
      };
      // Series can be an instrument (symbol) or another indicator (use its name)
      const symbol = getSeriesLabelForIndicator(
        minimalIndicator,
        instruments,
        indicators
      );
      const options =
        type === 'rolling'
          ? getRollingSeriesOptions(typedConfig.instrumentId, instruments, indicators)
          : undefined;
      const generatedName = getIndicatorDisplayName(minimalIndicator, symbol, options);
      const newIndicator: Indicator = {
        id: crypto.randomUUID(),
        name: getUniqueIndicatorName(generatedName, indicators),
        type: type,
        config: config as Record<string, any>,
        instrumentId: typedConfig.instrumentId,
      };

      returnedId = onIndicatorAdd(newIndicator);
    }

    setChoosers((prev) =>
      prev.map((c) =>
        c.id === id ? { ...c, indicatorId: returnedId, editMode: false } : c
      )
    );
    setAddEnabled(true);
  };

  const handleRemove = (id: number) => {
    const chooser = choosers.find((c) => c.id === id);
    if (chooser?.indicatorId) {
      useAppStore.getState().removeIndicator(chooser.indicatorId);
    }
    setChoosers((prev) => prev.filter((c) => c.id !== id));
    setAddEnabled(true);
  };

  return (
    <div className="mt-8 space-y-4">
      {choosers.map(({ id, editMode, indicatorId }) => {
        const indicator = indicatorId
          ? indicators.find((i) => i.id === indicatorId)
          : undefined;
        return (
          <IndicatorChooser
            key={id}
            indicators={Object.keys(editorMap)}
            indicatorLabels={indicatorLabels}
            onChange={(val) => console.log('Selected:', val)}
            onAccept={(name, config) => handleAccept(id, name, config)}
            onRemove={() => handleRemove(id)}
            editorMap={editorMap}
            editMode={editMode}
            indicatorId={indicatorId}
            displayName={indicator?.name}
            onNameChange={(indId, newName) => {
              const trimmed = newName.trim();
              if (!trimmed) {
                return 'Indicator name cannot be empty.';
              }
              const state = useAppStore.getState();
              const taken = state.indicators.some(
                (i) => i.id !== indId && i.name === trimmed
              );
              if (taken) {
                return 'This name already exists. Choose a unique name.';
              }
              useAppStore.getState().updateIndicator(indId, { name: trimmed });
              return null;
            }}
          />
        );
      })}

      <button
        type="button"
        onClick={handleAddClick}
        disabled={!primaryInstrumentId || !addEnabled}
        className="px-4 py-2 rounded-sm border border-rule bg-newsprint-panel text-ink hover:bg-newsprint-dark text-sm disabled:opacity-70 disabled:cursor-not-allowed"
      >
        Add additional indicators
      </button>
    </div>
  );
};

export default IndicatorSelector;